import React from "react";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { StorageContainerItem, getStorageContainerColumns } from "../helpers/ipstorage.constant";

interface StorageContainerSectionProps {
  containersList: StorageContainerItem[];
  setContainersList: React.Dispatch<React.SetStateAction<StorageContainerItem[]>>;
  containerError: string | null;
  setContainerError: React.Dispatch<React.SetStateAction<string | null>>;
  selectedBatch?: string;
  onRegisterValidate?: (validateFn: () => boolean) => void;
  isDisabled?: boolean;
}

// Unlike ContainerManagementSection (IP Inventory), containers here are NOT
// added/removed by the user — they are the fixed set of containers received
// against the selected batch. This section only lets the user assign a
// storage location/equipment/shelf/position/status/remarks per container.
// hideAction=true on CommonTableIndexWise removes the Action column entirely.
const StorageContainerSection: React.FC<StorageContainerSectionProps> = ({
  containersList,
  setContainersList,
  containerError,
  setContainerError,
  selectedBatch,
  onRegisterValidate,
  isDisabled = false,
}) => {
  const handleContainerChange = (
    index: number,
    field: keyof StorageContainerItem,
    val: any
  ) => {
    setContainerError(null);
    setContainersList((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: val };
      return next;
    });
  };

  const validateContainers = (): boolean => {
    if (!containersList || containersList.length === 0) {
      setContainerError("No containers found for the selected batch.");
      return false;
    }

    for (let i = 0; i < containersList.length; i++) {
      const item = containersList[i];
      const missing: string[] = [];

      if (!item.storagelocation) missing.push("Storage Location");
      if (!item.storageequipment) missing.push("Storage Equipment");
      if (!item.storagestatus) missing.push("Storage Status");

      if (missing.length > 0) {
        const containerNo = item.containernumber || String(i + 1).padStart(2, "0");
        const missingText = missing.join(", ").replace(/, ([^,]*)$/, " & $1");
        setContainerError(`Please select ${missingText} for Container ${containerNo}.`);
        return false;
      }
    }

    setContainerError(null);
    return true;
  };

  React.useEffect(() => {
    if (onRegisterValidate) {
      onRegisterValidate(validateContainers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containersList, onRegisterValidate]);

  const effectiveDisabled = isDisabled || !selectedBatch;

  const containerColumns = React.useMemo(
    () => getStorageContainerColumns(containersList, handleContainerChange, effectiveDisabled),
    [containersList, handleContainerChange, effectiveDisabled]
  );

  if (!selectedBatch) return null;

  return (
    <div className="mt-2">
      {containerError && (
        <div
          className="text-danger mb-2 d-flex align-items-center gap-1"
          style={{ fontSize: "11px", fontWeight: 500 }}
        >
          <i className="ri-error-warning-line me-1"></i>
          <span>{containerError}</span>
        </div>
      )}

      <CommonTableIndexWise
        columns={containerColumns}
        data={containersList}
        hideAction={true}
        paginationShow={false}
        emptyText="No containers found for the selected batch."
      />

      <div className="mt-2 p-2 border rounded-3 bg-light">
        <small className="text-muted" style={{ fontSize: "11px" }}>
          <i className="ri-information-line me-1"></i>
          Container list is based on the selected IP and Batch. All containers must be assigned a valid storage location.
        </small>
      </div>
    </div>
  );
};

export default StorageContainerSection;